import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import DashboardFilters from './components/DashboardFilters';
import EvolutionChart from './components/EvolutionChart';
import RankingPanel from './components/RankingPanel';
import ComparativeChart from './components/ComparativeChart';
import { executiveDashboardService } from '../../services/executiveDashboardService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const KPICard = ({ title, value, subtitle, icon, iconColor, valueColor, loading }) => (
  <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-4 shadow-sm hover:border-white/15 transition-all duration-200 flex flex-col justify-between min-h-[110px]">
    <div className="flex items-start justify-between mb-2">
      <span className="font-oranienbaum pr-2 text-lg text-[rgba(255,255,255,0.8)]">{title}</span>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${iconColor}18` }}>
        <Icon name={icon} size={16} style={{ color: iconColor }} />
      </div>
    </div>
    {loading ? (
      <div className="h-7 w-20 bg-white/5 rounded animate-pulse" />
    ) : (
      <p className="font-bold font-oranienbaum text-2xl" style={{ color: valueColor || '#ffffff' }}>
        {value}
      </p>
    )}
    {subtitle && (
      <p className="font-oranienbaum mt-1 truncate text-sm text-[rgba(255,255,255,0.4)]">{subtitle}</p>
    )}
  </div>
);

const ExecutiveDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    periodType: 'month',
    dateFrom: '',
    dateTo: '',
    singleDay: '',
    week: '',
    quarter: '',
    year: String(new Date()?.getFullYear()),
    month: `${new Date()?.getFullYear()}-${String(new Date()?.getMonth() + 1)?.padStart(2, '0')}`
  });

  const [kpis, setKpis] = useState({
    totalClients: 0,
    activeEmployees: 0,
    totalLoncheados: 0,
    totalSalaries: 0,
    totalRevenue: 0
  });

  const [costKpis, setCostKpis] = useState({
    costeSalaHoy: 0,
    costeSalaMes: 0,
    costeMedioPieza: 0,
    totalPagadoEmpleados: 0,
    rentabilidadNeta: 0
  });

  const [clientRankings, setClientRankings] = useState([]);
  const [employeeRankings, setEmployeeRankings] = useState([]);
  const [evolutionData, setEvolutionData] = useState([]);
  const [comparativeData, setComparativeData] = useState([]);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadDashboardData();
  }, [user, filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams = {};
      if (filters?.periodType === 'custom' && filters?.dateFrom && filters?.dateTo) {
        filterParams.dateFrom = filters?.dateFrom;
        filterParams.dateTo = filters?.dateTo;
      } else if (filters?.periodType === 'day' && filters?.singleDay) {
        filterParams.singleDay = filters?.singleDay;
      } else if (filters?.periodType === 'week' && filters?.week) {
        filterParams.week = filters?.week;
      } else if (filters?.periodType === 'month' && filters?.month) {
        filterParams.month = filters?.month;
      }

      const [kpisData, clientRankingsData, employeeRankingsData, evolutionDataResult, comparativeDataResult] =
        await Promise.all([
          executiveDashboardService?.getKPIs(filterParams),
          executiveDashboardService?.getClientRankings(filterParams),
          executiveDashboardService?.getEmployeeRankings(filterParams),
          executiveDashboardService?.getEvolutionData(filterParams, 'day'),
          executiveDashboardService?.getComparativeData(filterParams, 5),
        ]);

      setKpis(kpisData);
      setClientRankings(clientRankingsData);
      setEmployeeRankings(employeeRankingsData);
      setEvolutionData(evolutionDataResult);
      setComparativeData(comparativeDataResult);

      // Load cost KPIs from loncheados
      await loadCostKpis();
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err?.message || 'Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadCostKpis = async () => {
    try {
      const today = new Date();
      const todayStr = today?.toISOString()?.split('T')?.[0];
      const firstDayMes = new Date(today.getFullYear(), today.getMonth(), 1)?.toISOString()?.split('T')?.[0];
      const lastDayMes = new Date(today.getFullYear(), today.getMonth() + 1, 0)?.toISOString()?.split('T')?.[0];

      // Coste sala hoy
      const { data: hoy } = await supabase
        ?.from('loncheados')
        ?.select('tarifa, coste_sala, tipo, peso')
        ?.gte('created_at', todayStr + 'T00:00:00')
        ?.lte('created_at', todayStr + 'T23:59:59');

      // Coste sala mes
      const { data: mes } = await supabase
        ?.from('loncheados')
        ?.select('tarifa, coste_sala, tipo, peso')
        ?.gte('created_at', firstDayMes)
        ?.lte('created_at', lastDayMes + 'T23:59:59');

      // Total pagado a empleados (salarios)
      const { data: salarios } = await supabase
        ?.from('salarios')
        ?.select('salario')
        ?.gte('fecha', firstDayMes)
        ?.lte('fecha', lastDayMes);

      const calcCoste = (records) =>
        (records || [])?.reduce((sum, r) => {
          const v = parseFloat(r?.coste_sala) || parseFloat(r?.tarifa) || 0;
          return sum + v;
        }, 0);

      const costeSalaHoy = calcCoste(hoy);
      const costeSalaMes = calcCoste(mes);
      const totalPiezasMes = (mes || [])?.length;
      const costeMedioPieza = totalPiezasMes > 0 ? costeSalaMes / totalPiezasMes : 0;
      const totalPagadoEmpleados = (salarios || [])?.reduce((s, r) => s + (parseFloat(r?.salario) || 0), 0);

      setCostKpis({
        costeSalaHoy,
        costeSalaMes,
        costeMedioPieza,
        totalPagadoEmpleados,
        rentabilidadNeta: 0 // will be computed from main kpis
      });
    } catch (err) {
      console.warn('Error loading cost KPIs:', err?.message);
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' })?.format(value || 0);

  const netProfit = (kpis?.totalRevenue || 0) - (kpis?.totalSalaries || 0);
  const margin = kpis?.totalRevenue > 0 ? (netProfit / kpis?.totalRevenue) * 100 : 0;
  const avgPerLoncheado = kpis?.totalLoncheados > 0 ? kpis?.totalRevenue / kpis?.totalLoncheados : 0;

  const kpiRow1 = [
    { title: 'Ingresos Totales', value: formatCurrency(kpis?.totalRevenue), subtitle: 'Facturación del periodo', icon: 'TrendingUp', iconColor: '#3A7F6B', valueColor: '#3A7F6B' },
    { title: 'Beneficio Neto', value: formatCurrency(netProfit), subtitle: `Margen: ${margin?.toFixed(1)}%`, icon: 'DollarSign', iconColor: netProfit >= 0 ? '#3A7F6B' : '#E05252', valueColor: netProfit >= 0 ? '#3A7F6B' : '#E05252' },
    { title: 'Total Clientes', value: kpis?.totalClients || 0, subtitle: 'Clientes activos', icon: 'Users', iconColor: '#8097D6', valueColor: '#8097D6' },
    { title: 'Total Loncheados', value: kpis?.totalLoncheados || 0, subtitle: 'Piezas procesadas', icon: 'Package', iconColor: '#C9A24D', valueColor: '#C9A24D' },
  ];

  const kpiRow2 = [
    { title: 'Empleados Activos', value: kpis?.activeEmployees || 0, subtitle: 'Empleados trabajando', icon: 'UserCheck', iconColor: '#3A7F6B', valueColor: '#ffffff' },
    { title: 'Costes Laborales', value: formatCurrency(kpis?.totalSalaries), subtitle: 'Salarios del periodo', icon: 'TrendingDown', iconColor: '#E05252', valueColor: '#E05252' },
    { title: 'Media por Loncheado', value: formatCurrency(avgPerLoncheado), subtitle: 'Ingreso promedio', icon: 'BarChart3', iconColor: '#C9A24D', valueColor: '#C9A24D' },
    { title: 'Margen %', value: `${margin?.toFixed(1)}%`, subtitle: margin >= 30 ? 'Excelente' : margin >= 15 ? 'Bueno' : margin >= 0 ? 'Mejorable' : 'Negativo', icon: 'Percent', iconColor: margin >= 15 ? '#3A7F6B' : '#E05252', valueColor: margin >= 15 ? '#3A7F6B' : '#E05252' },
  ];

  const kpiRow3 = [
    { title: 'Coste Sala Hoy', value: formatCurrency(costKpis?.costeSalaHoy), subtitle: 'Coste de sala del día', icon: 'Building2', iconColor: '#D6EF4E', valueColor: '#D6EF4E' },
    { title: 'Coste Sala Mes', value: formatCurrency(costKpis?.costeSalaMes), subtitle: 'Coste acumulado del mes', icon: 'CalendarDays', iconColor: '#C9A24D', valueColor: '#C9A24D' },
    { title: 'Coste Medio por Pieza', value: formatCurrency(costKpis?.costeMedioPieza), subtitle: 'Promedio por loncheado', icon: 'Calculator', iconColor: '#8097D6', valueColor: '#8097D6' },
    { title: 'Total Pagado Empleados', value: formatCurrency(costKpis?.totalPagadoEmpleados), subtitle: 'Salarios del mes', icon: 'Wallet', iconColor: '#E05252', valueColor: '#E05252' },
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard Ejecutivo - Las Dos Doncellas</title>
        <meta name="description" content="Visión general y análisis ejecutivo del negocio" />
      </Helmet>

      <div className="min-h-screen bg-[#111111] px-4 lg:px-6 py-5">
        {/* Page Header */}
        <div className="mb-4">
          <h1 className="lg:text-3xl font-oranienbaum font-bold text-[#D6EF4E] text-[33px]">
            Dashboard Ejecutivo
          </h1>
          <p className="font-oranienbaum text-sm mt-0.5 text-[rgba(255,255,255,0.702)]">
            Visión general y análisis de rendimiento del negocio
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
            <p className="font-oranienbaum text-sm">{error}</p>
          </div>
        )}

        {/* Compact Filters Row */}
        <div className="mb-4">
          <DashboardFilters filters={filters} onFilterChange={setFilters} />
        </div>

        {/* KPI Grid Row 1 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          {kpiRow1?.map((kpi) => (
            <KPICard key={kpi?.title} {...kpi} loading={loading} />
          ))}
        </div>

        {/* KPI Grid Row 2 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {kpiRow2?.map((kpi) => (
            <KPICard key={kpi?.title} {...kpi} loading={loading} />
          ))}
        </div>

        {/* KPI Grid Row 3 — Costes de Sala */}
        <div className="mb-2">
          <h2 className="font-bold font-oranienbaum text-white/50 text-xs uppercase tracking-widest mb-2 px-0.5">
            Costes de Sala
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {kpiRow3?.map((kpi) => (
            <KPICard key={kpi?.title} {...kpi} loading={loading} />
          ))}
        </div>

        {/* Charts Row 1: Evolution (66%) + Cost Distribution (33%) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Evolution Chart — 2/3 width */}
          <div className="lg:col-span-2 bg-[#1A1A1A] rounded-xl border border-white/8 p-4 shadow-sm">
            <h2 className="font-bold font-oranienbaum text-[#D6EF4E] mb-3 text-[19px]">
              Evolución del Periodo
            </h2>
            {loading ? (
              <div className="flex items-center justify-center h-52">
                <Icon name="Loader2" size={32} className="text-[#D6EF4E] animate-spin" />
              </div>
            ) : (
              <EvolutionChart data={evolutionData} title="" />
            )}
          </div>

          {/* Cost Distribution — 1/3 width */}
          <div className="lg:col-span-1 bg-[#1A1A1A] rounded-xl border border-white/8 p-4 shadow-sm">
            <h2 className="font-bold font-oranienbaum text-[#D6EF4E] mb-3 text-lg">
              Distribución de Costes
            </h2>
            {loading ? (
              <div className="flex items-center justify-center h-52">
                <Icon name="Loader2" size={32} className="text-[#D6EF4E] animate-spin" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Simple cost breakdown */}
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-xs font-oranienbaum text-white/60">Ingresos</span>
                  <span className="text-sm font-bold font-oranienbaum text-[#3A7F6B]">{formatCurrency(kpis?.totalRevenue)}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-xs font-oranienbaum text-white/60">Costes Laborales</span>
                  <span className="text-sm font-bold font-oranienbaum text-[#E05252]">{formatCurrency(kpis?.totalSalaries)}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-xs font-oranienbaum text-white/60">Beneficio Neto</span>
                  <span className={`text-sm font-bold font-oranienbaum ${netProfit >= 0 ? 'text-[#3A7F6B]' : 'text-[#E05252]'}`}>
                    {formatCurrency(netProfit)}
                  </span>
                </div>
                {/* Visual bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] font-oranienbaum text-white/40 mb-1">
                    <span>Costes vs Ingresos</span>
                    <span>{kpis?.totalRevenue > 0 ? ((kpis?.totalSalaries / kpis?.totalRevenue) * 100)?.toFixed(1) : 0}%</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#E05252] to-[#C9A24D] rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(kpis?.totalRevenue > 0 ? (kpis?.totalSalaries / kpis?.totalRevenue) * 100 : 0, 100)}%` }}
                    />
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full bg-gradient-to-r from-[#3A7F6B] to-[#D6EF4E] rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(margin, 0)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] font-oranienbaum text-white/40 mt-1">
                    <span>Margen neto</span>
                    <span>{margin?.toFixed(1)}%</span>
                  </div>
                </div>
                {/* Loncheados stats */}
                <div className="mt-2 pt-2 border-t border-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-oranienbaum text-white/60">Loncheados</span>
                    <span className="text-sm font-bold font-oranienbaum text-[#C9A24D]">{kpis?.totalLoncheados || 0}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs font-oranienbaum text-white/60">Media/pieza</span>
                    <span className="text-sm font-bold font-oranienbaum text-white/80">{formatCurrency(avgPerLoncheado)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Charts Row 2: Rentabilidad por Cliente + Productividad por Empleado */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-4 shadow-sm">
            <h2 className="font-bold font-oranienbaum text-[#D6EF4E] mb-3 text-lg">
              Rentabilidad por Cliente
            </h2>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Icon name="Loader2" size={32} className="text-[#D6EF4E] animate-spin" />
              </div>
            ) : (
              <ComparativeChart
                data={comparativeData}
                title=""
                dataKey1="revenue"
                dataKey2="costs"
                label1="Facturación"
                label2="Costes"
              />
            )}
          </div>

          <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-4 shadow-sm">
            <h2 className="font-bold font-oranienbaum text-[#D6EF4E] mb-3 text-lg">
              Productividad por Empleado
            </h2>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Icon name="Loader2" size={32} className="text-[#D6EF4E] animate-spin" />
              </div>
            ) : (
              <RankingPanel
                data={employeeRankings}
                rankings={employeeRankings}
                type="employee"
                title=""
                icon="UserCheck"
                formatCurrency={formatCurrency}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExecutiveDashboard;